import { useState, useEffect, memo } from "react";
import { formatTimer } from "@/lib/timer-utils";

interface IsolatedStageTimerProps {
  stageStartTime: string | Date | null;
  className?: string;
}

/**
 * Isolated timer component that manages its own updates without affecting parent re-renders.
 * Uses React.memo to prevent unnecessary re-renders and internal state for timer updates.
 */
export const IsolatedStageTimer = memo(function IsolatedStageTimer({
  stageStartTime,
  className = "",
}: IsolatedStageTimerProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!stageStartTime) {
      setElapsed(0);
      return;
    }

    // Calculate initial elapsed time
    const startTime = stageStartTime instanceof Date 
      ? stageStartTime.getTime() 
      : new Date(stageStartTime).getTime();
    const updateElapsed = () => {
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      setElapsed(elapsedSeconds);
    };

    // Update immediately
    updateElapsed();

    // Set up interval to update every second
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [stageStartTime]);

  return (
    <span className={className}>
      {formatTimer(elapsed)}
    </span>
  );
});

interface IsolatedStageTotalTimerProps {
  stageStartTime: string | Date | null;
  previousTimeOnStage: number;
  className?: string;
}

/**
 * Isolated total timer component that shows current stage time + previous time.
 */
export const IsolatedStageTotalTimer = memo(function IsolatedStageTotalTimer({
  stageStartTime,
  previousTimeOnStage,
  className = "",
}: IsolatedStageTotalTimerProps) {
  const [currentSeconds, setCurrentSeconds] = useState(0);

  useEffect(() => {
    if (!stageStartTime) {
      setCurrentSeconds(0);
      return;
    }

    // Calculate initial elapsed time
    const startTime = stageStartTime instanceof Date 
      ? stageStartTime.getTime() 
      : new Date(stageStartTime).getTime();
    const updateElapsed = () => {
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      setCurrentSeconds(elapsedSeconds);
    };

    // Update immediately
    updateElapsed();

    // Set up interval to update every second
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [stageStartTime]);

  // Convert previousTimeOnStage (minutes) to seconds and add current seconds
  const totalSeconds = (previousTimeOnStage * 60) + currentSeconds;
  
  return (
    <span className={className}>
      {formatTimer(totalSeconds)}
    </span>
  );
});